import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2, RotateCcw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import DisqualifiedScreen from "@/components/DisqualifiedScreen";
import SecureUploadAndSign from "@/components/SecureUploadAndSign";
import CallbackCapture from "@/components/CallbackCapture";

const BOOKING_URL = process.env.REACT_APP_BOOKING_URL;

const POSITION_OPTIONS = [
  { label: "None", value: 0 },
  { label: "1 position", value: 1 },
  { label: "2 positions", value: 2 },
  { label: "3 or more", value: 3 },
];

const YES_NO = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];

const STEP_META = {
  revenue: { eyebrow: "Question 01 — Revenue", question: "What is your average gross monthly business revenue?" },
  credit: { eyebrow: "Question 02 — Credit", question: "What is your estimated personal credit score?" },
  liens: { eyebrow: "Question 03 — Encumbrances", question: "Any business or personal liens or bankruptcies in the past 5 years?" },
  positions: { eyebrow: "Question 04 — Exposure", question: "How many active funding positions are open right now?" },
  defaulted: { eyebrow: "Question 05 — Track record", question: "Have you ever defaulted on a business advance or loan?" },
  zbl: { eyebrow: "Follow-up — Resolution", question: "Do you have a Zero Balance Letter (ZBL) ready for that default?" },
};

export default function QualificationFunnel() {
  const [searchParams] = useSearchParams();
  const recheckId = searchParams.get("recheck");
  const [stepIdx, setStepIdx] = useState(0);
  const [revenue, setRevenue] = useState("");
  const [creditScore, setCreditScore] = useState("");
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState("");
  const [disqualified, setDisqualified] = useState(false);
  const [failReason, setFailReason] = useState("");
  const [leadId, setLeadId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const steps = answers.defaulted
    ? ["revenue", "credit", "liens", "positions", "defaulted", "zbl"]
    : ["revenue", "credit", "liens", "positions", "defaulted"];
  const stepKey = steps[stepIdx];
  const meta = STEP_META[stepKey];
  const progress = Math.min(((stepIdx + 1) / steps.length) * 100, 100);

  const fail = (reason) => {
    setFailReason(reason);
    setDisqualified(true);
  };

  const finalize = async (finalAnswers) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/leads", {
        monthly_revenue: Number(revenue),
        credit_score: Number(creditScore),
        liens_or_bankruptcies: finalAnswers.liens,
        active_positions: finalAnswers.positions,
        defaulted: finalAnswers.defaulted,
        zbl_ready: finalAnswers.defaulted ? finalAnswers.zbl : null,
        waitlist_id: recheckId || undefined,
      });
      setLeadId(data.id);
    } catch (e) {
      if (e?.response?.status === 422) {
        fail("underwriting");
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const continueNumeric = () => {
    setError("");
    if (stepKey === "revenue") {
      const v = Number(revenue.replace(/[^0-9.]/g, ""));
      if (!v || v <= 0) return setError("Enter your average gross monthly revenue.");
      if (v < 20000) return fail("revenue_under_20k");
      setRevenue(String(v));
    }
    if (stepKey === "credit") {
      const v = Number(creditScore.replace(/[^0-9]/g, ""));
      if (!v || v < 300 || v > 850) return setError("Enter a credit score between 300 and 850.");
      if (v < 500) return fail("credit_under_500");
      setCreditScore(String(v));
    }
    setStepIdx(stepIdx + 1);
  };

  const choose = (value) => {
    const next = { ...answers, [stepKey]: value };
    setAnswers(next);
    setTimeout(() => {
      if (stepKey === "liens" && value === true) return fail("liens_or_bankruptcy");
      if (stepKey === "defaulted" && value === false) return finalize(next);
      if (stepKey === "zbl") {
        return value === false ? fail("default_without_zbl") : finalize(next);
      }
      setStepIdx(stepIdx + 1);
    }, 280);
  };

  const restart = () => {
    setStepIdx(0);
    setRevenue("");
    setCreditScore("");
    setAnswers({});
    setError("");
    setLeadId(null);
    setDisqualified(false);
    setFailReason("");
  };

  const renderStepBody = () => {
    if (stepKey === "revenue" || stepKey === "credit") {
      const isRevenue = stepKey === "revenue";
      return (
        <form
          onSubmit={(e) => { e.preventDefault(); continueNumeric(); }}
          data-testid={`funnel-${stepKey}-form`}
        >
          <div className="relative">
            {isRevenue && (
              <span className="absolute left-5 top-1/2 -translate-y-1/2 text-zinc-500 font-display text-2xl">$</span>
            )}
            <input
              type="text"
              inputMode="numeric"
              autoFocus={stepIdx > 0}
              value={isRevenue ? revenue : creditScore}
              onChange={(e) => {
                setError("");
                (isRevenue ? setRevenue : setCreditScore)(e.target.value.replace(/[^0-9]/g, ""));
              }}
              placeholder={isRevenue ? "45,000" : "e.g. 640"}
              data-testid={`funnel-${stepKey}-input`}
              className={`w-full bg-[#07090D] border border-white/15 rounded-sm ${isRevenue ? "pl-11" : "pl-5"} pr-5 py-5 font-display text-2xl font-light text-zinc-100 placeholder:text-zinc-700 focus:outline-none focus:border-amber-500/60 transition-colors`}
            />
          </div>
          {isRevenue && (
            <p className="mt-3 text-xs font-mono-d tracking-[0.15em] uppercase text-zinc-600">
              Lender minimum · $20,000 / month
            </p>
          )}
          {stepKey === "credit" && (
            <p className="mt-3 text-xs font-mono-d tracking-[0.15em] uppercase text-zinc-600">
              Lender minimum · 500 · soft estimate only, no hard pull
            </p>
          )}
          {error && (
            <p className="mt-3 text-xs text-rose-400" data-testid="funnel-input-error">{error}</p>
          )}
          <button
            type="submit"
            data-testid="funnel-continue-button"
            className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-amber-500 hover:bg-amber-400 text-[#07090D] font-semibold text-sm px-8 py-4 rounded-sm transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98]"
          >
            Continue <ArrowRight className="w-4 h-4" />
          </button>
        </form>
      );
    }
    const options = stepKey === "positions" ? POSITION_OPTIONS : YES_NO;
    const selected = answers[stepKey];
    return (
      <div className={`grid grid-cols-1 ${stepKey === "positions" ? "sm:grid-cols-2" : "sm:grid-cols-2"} gap-3`}>
        {options.map((opt) => (
          <button
            key={opt.label}
            onClick={() => choose(opt.value)}
            disabled={submitting}
            data-testid={`funnel-option-${stepKey}-${String(opt.value).toLowerCase()}`}
            className={`group flex items-center justify-between border rounded-sm px-5 py-4 text-left text-sm transition-all duration-300 active:scale-[0.98] ${
              selected === opt.value
                ? "border-amber-500 bg-amber-500/10 text-zinc-100"
                : "border-white/10 bg-white/[0.02] text-zinc-300 hover:border-amber-500/40 hover:bg-amber-500/[0.04]"
            }`}
          >
            {opt.label}
            <ArrowRight className="w-4 h-4 text-zinc-600 opacity-0 group-hover:opacity-100 group-hover:text-amber-400 transition-all duration-300" />
          </button>
        ))}
      </div>
    );
  };

  return (
    <section id="pre-approval" className="py-24 sm:py-32 bg-[#0A0D12] border-t border-white/10" data-testid="qualification-funnel">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-14"
        >
          <p className="eyebrow mb-6">60-second pre-approval</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-normal tracking-tight text-zinc-100">
            Five questions. <span className="font-editorial italic text-amber-400">Zero hard credit pulls.</span>
          </h2>
        </motion.div>

        <div className="glass-panel rounded-md p-6 sm:p-10" data-testid="funnel-panel">
          {disqualified ? (
            <DisqualifiedScreen
              reason={failReason}
              onRestart={restart}
              snapshot={{ monthly_revenue: Number(revenue) || null, credit_score: Number(creditScore) || null }}
            />
          ) : leadId ? (
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="qualified-booking-screen">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-xs font-mono-d tracking-[0.2em] uppercase text-emerald-400">
                  Pre-approved — your file is with the syndicate
                </span>
              </div>
              <h3 className="font-display text-xl sm:text-2xl font-medium tracking-tight text-zinc-100 mb-2">
                Lock in your funding call.
              </h3>
              <p className="text-sm text-zinc-400 mb-6 leading-relaxed">
                Pick a time below — a senior broker will walk you through the competing term sheets
                your file attracts.
              </p>
              <CallbackCapture leadId={leadId} />
              <div className="rounded-sm overflow-hidden border border-white/15 bg-white" data-lenis-prevent>
                <iframe
                  src={BOOKING_URL}
                  title="Book your funding call"
                  data-testid="booking-widget"
                  className="w-full h-[600px]"
                  loading="lazy"
                />
              </div>
              <div className="border-t border-white/10 my-10" />
              <SecureUploadAndSign leadId={leadId} onRestart={restart} />
            </motion.div>
          ) : (
            <>
              {recheckId && stepIdx === 0 && (
                <div
                  className="mb-8 flex items-start gap-3 border border-amber-500/30 bg-amber-500/[0.06] rounded-sm px-4 py-3"
                  data-testid="recheck-banner"
                >
                  <RotateCcw className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-xs leading-relaxed text-zinc-300">
                    <span className="text-amber-400 font-semibold">Welcome back.</span> Let's re-check your file —
                    if your numbers have improved, you'll go straight to a broker's calendar.
                  </p>
                </div>
              )}
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-mono-d tracking-[0.2em] uppercase text-zinc-500" data-testid="funnel-step-indicator">
                  {meta.eyebrow}
                </span>
                <span className="text-xs font-mono-d text-zinc-500">{stepIdx + 1} / {steps.length}</span>
              </div>
              <div className="h-px bg-white/10 mb-10 relative overflow-hidden">
                <motion.div
                  className="absolute left-0 top-0 h-px bg-amber-500"
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  data-testid="funnel-progress-bar"
                />
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={stepKey}
                  initial={{ opacity: 0, x: 32 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -32 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                >
                  <h3 className="font-display text-xl sm:text-2xl font-medium tracking-tight text-zinc-100 mb-8" data-testid="funnel-question">
                    {meta.question}
                  </h3>
                  {renderStepBody()}
                </motion.div>
              </AnimatePresence>

              <div className="mt-10 flex items-center justify-between">
                <button
                  onClick={() => { setError(""); setStepIdx(Math.max(0, stepIdx - 1)); }}
                  disabled={stepIdx === 0 || submitting}
                  data-testid="funnel-back-button"
                  className="inline-flex items-center gap-2 text-xs font-mono-d tracking-[0.15em] uppercase text-zinc-500 hover:text-amber-400 disabled:opacity-30 disabled:hover:text-zinc-500 transition-colors duration-300"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Back
                </button>
                {submitting && (
                  <span className="text-xs font-mono-d tracking-[0.15em] uppercase text-amber-400 animate-pulse" data-testid="funnel-submitting">
                    <Loader2 className="w-3.5 h-3.5 inline animate-spin mr-2 -mt-0.5" />
                    Clearing underwriting…
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
